import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Mail, Phone, Save, X } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { CLINIC } from "@/lib/clinicInfo";

const STATUSES = ["new", "contacted", "scheduled", "done", "cancelled"];

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [appts, setAppts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyPost = {
    title: "", excerpt: "", content: "", cover_image: "", tags: "", category: "Eye Health",
    author: "Dr. Gurkiran Kaur", meta_title: "", meta_description: "", published: true,
  };

  useEffect(() => {
    const token = localStorage.getItem("nehchal_admin_token");
    if (!token) { nav("/admin"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [meR, apR, poR] = await Promise.all([
        api.get("/admin/me"),
        api.get("/admin/appointments"),
        api.get("/admin/blog/posts"),
      ]);
      setMe(meR.data);
      setAppts(apR.data.appointments || []);
      setPosts(poR.data.posts || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("nehchal_admin_token");
        nav("/admin");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem("nehchal_admin_token");
    localStorage.removeItem("nehchal_admin_email");
    nav("/admin");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      setAppts((prev) => prev.map((a) => a.id === id ? { ...a, status } : a));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const openEditor = (post) => {
    if (post) {
      setEditing({
        ...post,
        tags: (post.tags || []).join(", "),
      });
    } else {
      setEditing({ ...emptyPost });
    }
    setEditorOpen(true);
  };

  const savePost = async () => {
    if (!editing.title.trim() || !editing.excerpt.trim() || !editing.content.trim()) {
      toast.error("Title, excerpt, and content are required");
      return;
    }
    const payload = {
      title: editing.title.trim(),
      excerpt: editing.excerpt.trim(),
      content: editing.content,
      cover_image: editing.cover_image || null,
      tags: editing.tags.split(",").map((t) => t.trim()).filter(Boolean),
      category: editing.category.trim() || "Eye Health",
      author: editing.author.trim() || "Dr. Gurkiran Kaur",
      meta_title: editing.meta_title.trim() || null,
      meta_description: editing.meta_description.trim() || null,
      published: !!editing.published,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/blog/posts/${editing.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Post created");
      }
      setEditorOpen(false);
      const poR = await api.get("/admin/blog/posts");
      setPosts(poR.data.posts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <>
      <Seo title="Admin Dashboard" description="Nehchal Eye Centre admin" path="/admin/dashboard" noindex />
      <div className="min-h-screen bg-secondary/30" data-testid="admin-dashboard">
        <header className="bg-background border-b border-border">
          <div className="container-wide h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={CLINIC.logo} alt="Logo" className="h-10 w-auto object-contain" />
              <div className="leading-tight border-l border-border pl-3">
                <div className="font-serif text-base text-primary">Nehchal Admin</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">{me?.email || "…"}</div>
              </div>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </header>

        <div className="container-wide py-10">
          <Tabs defaultValue="appointments">
            <TabsList data-testid="admin-tabs">
              <TabsTrigger value="appointments" data-testid="tab-appointments">
                Appointments ({appts.length})
              </TabsTrigger>
              <TabsTrigger value="blog" data-testid="tab-blog">
                Blog ({posts.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="appointments" className="mt-6">
              {loading ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : appts.length === 0 ? (
                <p className="text-muted-foreground">No appointments yet.</p>
              ) : (
                <div className="grid gap-3">
                  {appts.map((a) => (
                    <div key={a.id} className="rounded-lg border border-border bg-card p-4" data-testid={`appt-row-${a.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">{a.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {a.service} · {formatDate(a.created_at)}
                          </div>
                        </div>
                        <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                          <SelectTrigger className="w-40" data-testid={`appt-status-${a.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-4 text-sm text-foreground/80">
                        <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 hover:text-primary">
                          <Phone className="h-3.5 w-3.5" /> {a.phone}
                        </a>
                        {a.email && (
                          <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 hover:text-primary">
                            <Mail className="h-3.5 w-3.5" /> {a.email}
                          </a>
                        )}
                      </div>
                      {a.message && (
                        <p className="mt-2 text-sm text-foreground/70 border-l-2 border-primary/30 pl-3">
                          {a.message}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="blog" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-muted-foreground">{posts.length} article{posts.length !== 1 ? "s" : ""}</div>
                <button
                  onClick={() => openEditor(null)}
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90"
                  data-testid="admin-new-post-btn"
                >
                  <Plus className="h-4 w-4" /> New Post
                </button>
              </div>
              <div className="grid gap-3">
                {posts.map((p) => (
                  <div key={p.id} className="rounded-lg border border-border bg-card p-4" data-testid={`post-row-${p.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">{p.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {p.category} · {formatDate(p.created_at)} · {p.published ? "Published" : "Draft"} · /{p.slug}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditor(p)} className="rounded-full border border-border p-2 hover:bg-secondary" data-testid={`edit-post-${p.id}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => deletePost(p.id)} className="rounded-full border border-border p-2 hover:bg-destructive/10 text-destructive" data-testid={`delete-post-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-editor-dialog">
            <DialogHeader>
              <DialogTitle>{editing?.id ? "Edit Post" : "New Post"}</DialogTitle>
            </DialogHeader>
            {editing && (
              <div className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="editor-title" />
                </div>
                <div>
                  <Label>Excerpt * (short summary for cards & SEO)</Label>
                  <Textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} data-testid="editor-excerpt" />
                </div>
                <div>
                  <Label>Content * (HTML allowed: p, h2, h3, ul, ol, li, a, strong, em, blockquote)</Label>
                  <Textarea rows={12} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="editor-content" className="font-mono text-xs" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                  </div>
                  <div>
                    <Label>Tags (comma-separated)</Label>
                    <Input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Cover Image URL</Label>
                  <Input value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>SEO Meta Title (optional)</Label>
                    <Input value={editing.meta_title} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} />
                  </div>
                  <div>
                    <Label>SEO Meta Description (optional)</Label>
                    <Textarea rows={2} value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="published"
                    checked={editing.published}
                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                  />
                  <Label htmlFor="published" className="cursor-pointer">Published</Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <button onClick={() => setEditorOpen(false)} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary inline-flex items-center gap-2">
                <X className="h-4 w-4" /> Cancel
              </button>
              <button onClick={savePost} className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 inline-flex items-center gap-2" data-testid="editor-save">
                <Save className="h-4 w-4" /> Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
